// src/pages/ResetPasswordVerify.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import Meta from "../components/Meta";
import Container from "../components/Container";
import { useLocation, useNavigate, Link, useSearchParams } from "react-router-dom";
import { IoArrowBack } from "react-icons/io5";

import kuptoLogo from "../images/logo/kupto logobb-8.png";
import "./styles/resetPasswordVerify.css";

// ✅ use your existing app base_url
import { base_url } from "../utils/axiosConfig";

/**
 * ✅ IMPORTANT
 * Backend routes you currently have in userRoute.js:
 * - POST /user/forgot-password-code
 * - PUT  /user/reset-password-code
 *
 * You do NOT yet have:
 * - POST /user/verify-reset-code
 *
 * Since you already decided to add verifyResetCodeTok in the slice + service,
 * we keep this endpoint as:
 *   POST `${base_url}user/verify-reset-code`
 */
const CODE_LEN = 6;

const isValidEmail = (v) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());

const maskEmail = (email) => {
  const s = String(email || "").trim();
  if (!s.includes("@")) return s;
  const [u, d] = s.split("@");
  if (!u || !d) return s;
  const head = u.slice(0, 1);
  return `${head}${"*".repeat(Math.max(1, u.length - 1))}@${d}`;
};

// ✅ Backend endpoint (must exist in your backend routes)
const VERIFY_RESET_CODE_URL = `${base_url}user/verify-reset-code`;

const extractResetToken = (res) => {
  return (
    res?.token ||
    res?.resetToken ||
    res?.data?.token ||
    res?.data?.resetToken ||
    res?.payload?.token ||
    res?.payload?.resetToken ||
    ""
  );
};

const ResetPasswordVerify = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sp] = useSearchParams();

  // ✅ email can come from location.state.email or ?email=
  const emailFromState = location?.state?.email || location?.state?.identity || "";
  const emailFromQuery = sp.get("email") || sp.get("identity") || "";

  const email = useMemo(
    () => String(emailFromState || emailFromQuery || "").trim().toLowerCase(),
    [emailFromState, emailFromQuery]
  );

  // ✅ if user opened directly without email
  useEffect(() => {
    if (!email) navigate("/forgot-password", { replace: true });
  }, [email, navigate]);

  const masked = useMemo(() => maskEmail(email), [email]);

  // loader + errors
  const [isLoading, setIsLoading] = useState(false);
  const [apiErr, setApiErr] = useState("");
  const [infoMsg, setInfoMsg] = useState("");

  // code inputs
  const [codeArr, setCodeArr] = useState(Array(CODE_LEN).fill(""));
  const inputsRef = useRef([]);

  const code = codeArr.join("");
  const codeComplete = code.length === CODE_LEN && !codeArr.includes("");

  const focusIndex = (i) => {
    const el = inputsRef.current[i];
    if (el) el.focus();
  };

  // focus first input on mount
  useEffect(() => {
    const t = setTimeout(() => focusIndex(0), 50);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setDigit = (i, val) => {
    const digit = String(val || "").replace(/[^\d]/g, "").slice(0, 1);
    setCodeArr((prev) => {
      const next = [...prev];
      next[i] = digit;
      return next;
    });
    if (digit && i < CODE_LEN - 1) focusIndex(i + 1);
  };

  const onKeyDown = (i, e) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      setCodeArr((prev) => {
        const next = [...prev];
        if (next[i]) {
          next[i] = "";
        } else if (i > 0) {
          next[i - 1] = "";
          setTimeout(() => focusIndex(i - 1), 0);
        }
        return next;
      });
    }
    if (e.key === "ArrowLeft" && i > 0) focusIndex(i - 1);
    if (e.key === "ArrowRight" && i < CODE_LEN - 1) focusIndex(i + 1);
  };

  const onPaste = (e) => {
    const text = (e.clipboardData?.getData("text") || "").replace(/[^\d]/g, "");
    if (!text) return;
    e.preventDefault();

    const slice = text.slice(0, CODE_LEN).split("");
    setCodeArr(() => {
      const next = Array(CODE_LEN).fill("");
      for (let i = 0; i < CODE_LEN; i++) next[i] = slice[i] || "";
      return next;
    });

    const lastFilled = Math.min(slice.length, CODE_LEN) - 1;
    setTimeout(() => focusIndex(Math.max(0, lastFilled)), 0);
  };

  const canSubmit = Boolean(email) && isValidEmail(email) && codeComplete && !isLoading;

  const verifyResetCode = useCallback(
    async (e) => {
      e.preventDefault();
      setApiErr("");
      setInfoMsg("");

      if (!email || !isValidEmail(email)) {
        setApiErr("Missing or invalid email. Please go back and request a reset again.");
        return;
      }
      if (!codeComplete) {
        setApiErr("Please enter the 6-digit reset code.");
        return;
      }

      try {
        setIsLoading(true);

        const res = await fetch(VERIFY_RESET_CODE_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ email, code }),
        });

        let payload = null;
        try {
          payload = await res.json();
        } catch {
          payload = null;
        }

        if (!res.ok) {
          const msg =
            payload?.message ||
            payload?.error ||
            payload?.msg ||
            `Verification failed (${res.status})`;
          throw new Error(msg);
        }

        setIsLoading(false);

        const token = extractResetToken(payload);
        const finalToken = token || code;

        navigate(`/reset-password/${encodeURIComponent(finalToken)}`, {
          replace: true,
          state: { token: finalToken, email },
        });
      } catch (err) {
        console.error(err);
        setIsLoading(false);
        setApiErr(err?.message || "Invalid or expired code. Please try again.");
      }
    },
    [email, code, codeComplete, navigate]
  );

  const resend = useCallback(() => {
    navigate("/forgot-password", {
      replace: true,
      state: { identity: email, type: "email" },
    });
  }, [navigate, email]);

  return (
    <>
      <Meta title="Reset Password Verification" />

      {/* ✅ FULL-WIDTH TOP LOADER (app + desktop) */}
      {isLoading ? (
        <div className="ku-rpv-topLoader" role="status" aria-label="Loading">
          <span className="ku-rpv-topTrack" aria-hidden="true">
            <span className="ku-rpv-topBar" />
          </span>
        </div>
      ) : null}

      <Container class1="ku-rpv-wrap">
        <div className="ku-rpv-shell">
          <div className={`ku-rpv-card ${isLoading ? "is-busy" : ""}`}>
            <button
              type="button"
              className="ku-rpv-back ku-rpv-back--appOnly"
              onClick={() => navigate(-1)}
              aria-label="Go back"
              disabled={isLoading}
            >
              <IoArrowBack />
            </button>

            {/* LEFT */}
            <div className="ku-rpv-left">
              <div className="ku-rpv-brand" aria-hidden="true">
                <div className="ku-rpv-mark">
                  <img
                    src={kuptoLogo}
                    alt="Kupto"
                    className="ku-rpv-logo"
                    draggable="false"
                  />
                </div>
              </div>

              <div className="ku-rpv-head">
                <h1 className="ku-rpv-title">Verify reset code</h1>
                <p className="ku-rpv-sub">
                  Enter the 6-digit reset code sent to <b>{masked}</b>.
                </p>
              </div>

              <form className="ku-rpv-form" onSubmit={verifyResetCode} noValidate>
                <div className="ku-rpv-block">
                  <p className="ku-rpv-label">Reset code</p>

                  <div className="ku-rpv-codeRow" onPaste={onPaste}>
                    {codeArr.map((d, i) => (
                      <input
                        key={i}
                        ref={(el) => (inputsRef.current[i] = el)}
                        className="ku-rpv-codeInput"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        value={d}
                        onChange={(e) => setDigit(i, e.target.value)}
                        onKeyDown={(e) => onKeyDown(i, e)}
                        maxLength={1}
                        disabled={isLoading}
                        aria-label={`Digit ${i + 1}`}
                      />
                    ))}
                  </div>
                </div>

                {apiErr ? <div className="ku-rpv-error">{apiErr}</div> : null}
                {infoMsg ? <div className="ku-rpv-info">{infoMsg}</div> : null}

                <button type="submit" className="ku-rpv-btn" disabled={!canSubmit}>
                  {isLoading ? "Verifying..." : "Verify"}
                </button>

                <div className="ku-rpv-actions">
                  <Link className="ku-rpv-linkBtn" to="/login">
                    Back to Sign in
                  </Link>
                  <span className="ku-rpv-dot">•</span>

                  <button
                    type="button"
                    className="ku-rpv-linkBtn"
                    onClick={resend}
                    disabled={isLoading}
                    style={{ background: "transparent", border: 0, padding: 0 }}
                  >
                    Request new code
                  </button>
                </div>

                <p className="ku-rpv-legal">
                  By continuing you agree to Kupto’s{" "}
                  <Link to="/terms-and-conditions">Terms and Conditions</Link>
                </p>
              </form>

              <span className="ku-rpv-glow ku-rpv-glow--1" />
              <span className="ku-rpv-glow ku-rpv-glow--2" />
            </div>

            {/* RIGHT (desktop only). Hidden on app via CSS */}
            <div className="ku-rpv-right ku-rpv-right--desktopOnly">
              <h3 className="ku-rpv-rightTitle">Tips</h3>

              <div className="ku-rpv-tipList">
                <div className="ku-rpv-tip">
                  <div className="ku-rpv-tipIcon">📩</div>
                  <div className="ku-rpv-tipTxt">
                    <p className="ku-rpv-tipH">Check email</p>
                    <p className="ku-rpv-tipP">
                      Look for a 6-digit code from Kupto. Check spam/junk too.
                    </p>
                  </div>
                </div>

                <div className="ku-rpv-tip">
                  <div className="ku-rpv-tipIcon">⏳</div>
                  <div className="ku-rpv-tipTxt">
                    <p className="ku-rpv-tipH">Code expires</p>
                    <p className="ku-rpv-tipP">If your code expires, request a new one.</p>
                  </div>
                </div>

                <div className="ku-rpv-tip">
                  <div className="ku-rpv-tipIcon">📶</div>
                  <div className="ku-rpv-tipTxt">
                    <p className="ku-rpv-tipH">Network</p>
                    <p className="ku-rpv-tipP">Weak network can delay verification requests.</p>
                  </div>
                </div>

                <div className="ku-rpv-tip">
                  <div className="ku-rpv-tipIcon">🔐</div>
                  <div className="ku-rpv-tipTxt">
                    <p className="ku-rpv-tipH">Secure</p>
                    <p className="ku-rpv-tipP">Don’t share your reset code with anyone.</p>
                  </div>
                </div>
              </div>
            </div>
            {/* END RIGHT */}
          </div>
        </div>
      </Container>
    </>
  );
};

export default ResetPasswordVerify;
